import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import "./Navbar.css";
import logo from "../assests/logo/logo.webp";
import registration  from "../assests/img/registration.png";
import studentlogin from "../assests/img/studentlogin.png";

const BASE_URL = process.env.REACT_APP_API_URL || "http://192.168.0.57:5001/api";

const MAX_VISIBLE = 7;
// const [setSaving] = useState(false);

function slugify(name = "") {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

// parentPath is the already-built slug path of everything above this item
function pageHref(item, parentPath = "") {
  if (item.page_url) return item.page_url;
  const slug = slugify(item.page_name);
  return parentPath ? `/${parentPath}/${slug}` : `/${slug}`;
}

// ── Skeleton ─────────────────────────────────────────────────────────────────
function NavSkeleton() {
  return (
    <ul className="nav-list desktop-nav" style={{ marginBottom: 0 }}>
      {Array.from({ length: MAX_VISIBLE }).map((_, i) => (
        <li key={i} className="nav-item">
          <span className="nav-skeleton-item skeleton" style={{ width: `${55 + i * 8}px` }} />
        </li>
      ))}
    </ul>
  );
}

// ── Recursive flyout submenu (any depth, level 3+) ────────────────────────────
// Renders itself, and if any of its own items have children, renders another
// FlyoutSubmenu recursively — so nesting is no longer capped at one level.
// Also measures itself against the viewport on mount and flips upward
// (anchors to its bottom edge instead of its top edge) when it would
// otherwise overflow off the bottom of the screen.
function FlyoutSubmenu({ items, parentPath = "", alignLeft = false, onNavigate }) {
  const ref = useRef(null);
  const [flip, setFlip] = useState(false);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.bottom > window.innerHeight) setFlip(true);
  }, []);

  if (!items?.length) return null;

  return (
    <ul
      ref={ref}
      className={`sub-dropdown${alignLeft ? " sub-dropdown-left" : ""}${flip ? " flip-up" : ""}`}
    >
      {items.map(item => {
        const itemPath = parentPath ? `${parentPath}/${slugify(item.page_name)}` : slugify(item.page_name);
        const hasChildren = item.children?.length > 0;
        return (
          <li
            key={item.page_id}
            className={`dropdown-item${hasChildren ? " has-sub" : ""}`}
            onMouseEnter={() => hasChildren && setOpenId(item.page_id)}
            onMouseLeave={() => setOpenId(null)}
          >
            <Link
              to={pageHref(item, parentPath)}
              className="dropdown-link sub-dropdown-link"
              onClick={onNavigate}
            >
              {item.page_name}
              {hasChildren && <span className="sub-chevron">&rsaquo;</span>}
            </Link>
            {hasChildren && openId === item.page_id && (
              <FlyoutSubmenu
                items={item.children}
                parentPath={itemPath}
                alignLeft={alignLeft}
                onNavigate={onNavigate}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

// ── Level-2 dropdown ──────────────────────────────────────────────────────────
function Dropdown({ items, alignRight = false, parentPath = "" }) {
  const [hoveredSub, setHoveredSub] = useState(null);
  if (!items?.length) return null;
  return (
    <ul className={`dropdown${alignRight ? " dropdown-right" : ""}`}>
      {items.map(child => {
        const childPath = parentPath ? `${parentPath}/${slugify(child.page_name)}` : slugify(child.page_name);
        return (
          <li
            key={child.page_id}
            className={`dropdown-item${child.children?.length ? " has-sub" : ""}`}
            onMouseEnter={() => child.children?.length && setHoveredSub(child.page_id)}
            onMouseLeave={() => setHoveredSub(null)}
          >
            <Link to={pageHref(child, parentPath)} className="dropdown-link">
              {child.page_name}
              {child.children?.length ? <span className="sub-chevron">&rsaquo;</span> : null}
            </Link>
            {child.children?.length > 0 && hoveredSub === child.page_id && (
              <FlyoutSubmenu items={child.children} parentPath={childPath} alignLeft={alignRight} />
            )}
          </li>
        );
      })}
    </ul>
  );
}

// ── Single desktop nav item ───────────────────────────────────────────────────
function NavItem({ menu, isActive, onEnter, onLeave, alignRight }) {
  const menuSlug = slugify(menu.page_name);
  return (
    <li
      className={`nav-item${menu.children?.length ? " has-dropdown" : ""}${isActive ? " active" : ""}`}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      {menu.children?.length ? (
        <button className="nav-link">
          {menu.page_name}
          <span className="chevron">▾</span>
        </button>
      ) : (
        <Link to={pageHref(menu)} className="nav-link">{menu.page_name}</Link>
      )}
      {menu.children?.length > 0 && isActive && (
        <Dropdown items={menu.children} alignRight={alignRight} parentPath={menuSlug} />
      )}
    </li>
  );
}

// ── Mobile accordion ──────────────────────────────────────────────────────────
function MobileMenu({ items }) {
  const [openMenus, setOpenMenus] = useState({});
  const [openSubs,  setOpenSubs]  = useState({});
  const toggle = (id, setter) => setter(p => ({ ...p, [id]: !p[id] }));

  return (
    <ul className="nav-list open">
      {items.map(menu => {
        const menuSlug = slugify(menu.page_name);
        return (
          <li key={menu.page_id} className={`nav-item${menu.children?.length ? " has-dropdown" : ""}`}>
            {menu.children?.length ? (
              <>
                <button className="nav-link" onClick={() => toggle(menu.page_id, setOpenMenus)}>
                  {menu.page_name}
                  <span className="chevron">{openMenus[menu.page_id] ? "▴" : "▾"}</span>
                </button>
                {openMenus[menu.page_id] && (
                  <ul className="dropdown">
                    {menu.children.map(child => {
                      const childPath = `${menuSlug}/${slugify(child.page_name)}`;
                      return (
                        <li key={child.page_id} className={`dropdown-item${child.children?.length ? " has-sub" : ""}`}>
                          {child.children?.length ? (
                            <>
                              <button className="dropdown-link mobile-sub-toggle" onClick={() => toggle(child.page_id, setOpenSubs)}>
                                {child.page_name}
                                <span className="sub-chevron">{openSubs[child.page_id] ? "▴" : "▾"}</span>
                              </button>
                              {openSubs[child.page_id] && (
                                <MobileSubTree
                                  items={child.children}
                                  parentPath={childPath}
                                />
                              )}
                            </>
                          ) : (
                            <Link to={pageHref(child, menuSlug)} className="dropdown-link">{child.page_name}</Link>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </>
            ) : (
              <Link to={pageHref(menu)} className="nav-link">{menu.page_name}</Link>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// ── Recursive mobile sub-tree (any depth), used inside the accordion ─────────
function MobileSubTree({ items, parentPath = "" }) {
  const [openIds, setOpenIds] = useState({});
  const toggle = id => setOpenIds(p => ({ ...p, [id]: !p[id] }));

  if (!items?.length) return null;

  return (
    <ul className="sub-dropdown mobile-sub">
      {items.map(item => {
        const itemPath = parentPath ? `${parentPath}/${slugify(item.page_name)}` : slugify(item.page_name);
        const hasChildren = item.children?.length > 0;
        return (
          <li key={item.page_id}>
            {hasChildren ? (
              <>
                <button className="dropdown-link mobile-sub-toggle" onClick={() => toggle(item.page_id)}>
                  {item.page_name}
                  <span className="sub-chevron">{openIds[item.page_id] ? "▴" : "▾"}</span>
                </button>
                {openIds[item.page_id] && (
                  <MobileSubTree items={item.children} parentPath={itemPath} />
                )}
              </>
            ) : (
              <Link to={pageHref(item, parentPath)} className="dropdown-link sub-dropdown-link">
                {item.page_name}
              </Link>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// ── "More" overflow dropdown ──────────────────────────────────────────────────
// function MoreDropdown({ items, isActive, onOpen, onClose }) {
//   const [activeSub, setActiveSub] = useState(null);
//   const ref = useRef(null);

//   useEffect(() => {
//     if (!isActive) return;
//     const fn = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
//     document.addEventListener("mousedown", fn);
//     return () => document.removeEventListener("mousedown", fn);
//   }, [isActive, onClose]);

//   return (
//     <li ref={ref} className={`nav-item has-dropdown${isActive ? " active" : ""}`}>
//       <button className="nav-link" onClick={() => (isActive ? onClose() : onOpen())}>
//         More <span className="chevron">{isActive ? "\u25b4" : "\u25be"}</span>
//       </button>

//       {isActive && (
//         <ul className="dropdown dropdown-right more-dropdown">
//           {items.map(menu => {
//             const menuSlug = slugify(menu.page_name);
//             const hasChildren = menu.children?.length > 0;
//             return (
//               <li
//                 key={menu.page_id}
//                 className={`dropdown-item${hasChildren ? " has-sub" : ""}`}
//                 onMouseEnter={() => hasChildren && setActiveSub(menu.page_id)}
//                 onMouseLeave={() => setActiveSub(null)}
//               >
//                 {hasChildren ? (
//                   <span className="dropdown-link more-parent">
//                     {menu.page_name}
//                     <span className="sub-chevron">&#x203a;</span>
//                   </span>
//                 ) : (
//                   <Link to={pageHref(menu)} className="dropdown-link" onClick={onClose}>
//                     {menu.page_name}
//                   </Link>
//                 )}
//                 {hasChildren && activeSub === menu.page_id && (
//                   <FlyoutSubmenu
//                     items={menu.children}
//                     parentPath={menuSlug}
//                     alignLeft
//                     onNavigate={onClose}
//                   />
//                 )}
//               </li>
//             );
//           })}
//         </ul>
//       )}
//     </li>
//   );
// }

// ── "More" overflow dropdown ──────────────────────────────────────────────────
function MoreDropdown({ items, isActive, onEnter, onLeave }) {
  const [activeSub, setActiveSub] = useState(null);

  return (
    <li
      className={`nav-item has-dropdown${isActive ? " active" : ""}`}
      onMouseEnter={onEnter}
      onMouseLeave={() => { onLeave(); setActiveSub(null); }}
    >
      <button className="nav-link">
        More <span className="chevron">{isActive ? "\u25b4" : "\u25be"}</span>
      </button>

      {isActive && (
        <ul className="dropdown dropdown-right more-dropdown">
          {items.map(menu => {
            const menuSlug = slugify(menu.page_name);
            const hasChildren = menu.children?.length > 0;
            return (
              <li
                key={menu.page_id}
                className={`dropdown-item${hasChildren ? " has-sub" : ""}`}
                onMouseEnter={() => hasChildren && setActiveSub(menu.page_id)}
                onMouseLeave={() => setActiveSub(null)}
              >
                {hasChildren ? (
                  <span className="dropdown-link more-parent">
                    {menu.page_name}
                    <span className="sub-chevron">&#x203a;</span>
                  </span>
                ) : (
                  <Link to={pageHref(menu)} className="dropdown-link">
                    {menu.page_name}
                  </Link>
                )}
                {hasChildren && activeSub === menu.page_id && (
                  <FlyoutSubmenu
                    items={menu.children}
                    parentPath={menuSlug}
                    alignLeft
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}

// ── Main Navbar ───────────────────────────────────────────────────────────────
export default function Navbar() {
  const [menuTree,       setMenuTree]       = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [mobileOpen,     setMobileOpen]     = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [scrolled,       setScrolled]       = useState(false);

  const location = useLocation();
  const navRef   = useRef(null);

  const visibleMenus  = menuTree.slice(0, MAX_VISIBLE);
  const overflowMenus = menuTree.slice(MAX_VISIBLE);

  // useEffect(() => {
  //   const controller = new AbortController();
  //   fetch(`${BASE_URL}/menus`, { signal: controller.signal })
  //     .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
  //     .then(data => setMenuTree(Array.isArray(data) ? data : []))
  //     .catch(err => { if (err.name !== "AbortError") console.error("Menu fetch failed:", err.message); })
  //     .finally(() => setLoading(false));
  //   return () => controller.abort();
  // }, []);
//modify the code on 11_09_2026 due to menu not shows sometimes
  useEffect(() => {
  const controller = new AbortController();

  const fetchMenus = async (attempt = 1) => {
    try {
      const response = await fetch(`${BASE_URL}/menus`, {
        signal: controller.signal,
        cache: "no-store",
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      if (Array.isArray(data)) {
        setMenuTree(data);
      } else {
        console.error("Invalid menu API response:", data);
        setMenuTree([]);
      }

      setLoading(false);
    } catch (err) {
      if (err.name === "AbortError") {
        return;
      }

      console.error(`Menu fetch failed (attempt ${attempt}):`, err.message);

      // Retry up to 3 times
      if (attempt < 3) {
        setTimeout(() => {
          if (!controller.signal.aborted) {
            fetchMenus(attempt + 1);
          }
        }, 1000);
      } else {
        setLoading(false);
      }
    }
  };

  fetchMenus();

  return () => controller.abort();
}, []);





  useEffect(() => { setMobileOpen(false); setActiveDropdown(null); }, [location]);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    const fn = e => { if (navRef.current && !navRef.current.contains(e.target)) setActiveDropdown(null); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  return (
    <>
      <div className="topstrip">
        <div className="container topstrip-inner">
          <span>🏛 Govt. of Odisha &nbsp;|&nbsp; Dept. of Health & Family Welfare</span>
          <div className="topstrip-links">
            <Link to="/">Home</Link>
            <span className="divider">|</span>
            <Link to="/site-map">Site map</Link>
            <span className="divider">|</span>
            <Link to="/web-link">Links</Link>
            <span className="divider">|</span>
            <Link to="/contact">Contact Us</Link>
            {/* <a href="/contact">Contact Us</a> */}


          </div>
        </div>
      </div>

      <header className="brand-header">
        <div className="container brand-inner">
          {/* <div className="brand-logo">
            <img src={logo} alt="MKCG Logo" />
          </div> */}

          <div className="brand-logo">
            <Link to="/">
              <img src={logo} alt="MKCG Logo" />
            </Link>
          </div>
          <div className="brand-text">
            <h1 className="brand-name">
              M.K.C.G. Medical College, Berhampur, Odisha, India
            </h1>
            <h2 className="brand-name-odia">
              ମହାରାଜା କୃଷ୍ଣଚନ୍ଦ୍ର ଗଜପତି ଭେଷଜ ମହାବିଦ୍ୟାଳୟ, ବ୍ରହ୍ମପୁର
            </h2>
            <p className="brand-govt">
              (GOVT OF ODISHA)
            </p>
          </div>



        </div>
    <div className="msg_sub_sec"> <Link to="/StudentRegistration"><p className="registration">  <img src={registration } alt="registration " />
    New Registration
    </p></Link>

         <Link to="/StudentLogin"><p className="Student-Login"> <img src={studentlogin} alt="studentlogin" />
     Student-Login
     </p></Link></div>
    {/*<div className="msg_sub_sec"> <p style={{background: "rgb(52, 82, 113)", color: "#fff", padding: "7px 10px",
borderRadius: "38px"}}>  <img src={opd} alt="opd" /> OPD : 0680-2290137</p>
<div className="clearfix"></div>
         <p style={{background: "#0d6bcc", color: "#fff", padding: "7px 10px",
borderRadius: "38px", marginTop: "-10px"}}> <img src={cc} alt="opd" />  Casualty : 0680-2290851</p></div>*/}
      </header>

      <nav ref={navRef} className={`main-nav${scrolled ? " sticky" : ""}`}>
        <div className="container nav-inner">
          <button className="hamburger" onClick={() => setMobileOpen(o => !o)} aria-label="Toggle menu">
            <span className={mobileOpen ? "open" : ""} />
            <span className={mobileOpen ? "open" : ""} />
            <span className={mobileOpen ? "open" : ""} />
          </button>

          {loading ? <NavSkeleton /> : (
            <ul className="nav-list desktop-nav"  style={{ marginBottom: 0 }}>
              {visibleMenus.map((menu, i) => (
                <NavItem
                  key={menu.page_id}
                  menu={menu}
                  isActive={activeDropdown === i}
                  onEnter={() => menu.children?.length && setActiveDropdown(i)}
                  onLeave={() => setActiveDropdown(null)}
                  alignRight={false}
                />
              ))}

              {/* {overflowMenus.length > 0 && (
                <MoreDropdown
                  items={overflowMenus}
                  isActive={activeDropdown === "more"}
                  onOpen={() => setActiveDropdown("more")}
                  onClose={() => setActiveDropdown(null)}
                />
              )} */}
              {overflowMenus.length > 0 && (
                <MoreDropdown
                  items={overflowMenus}
                  isActive={activeDropdown === "more"}
                  onEnter={() => setActiveDropdown("more")}
                  onLeave={() => setActiveDropdown(null)}
                />
              )}
            </ul>
          )}

          {mobileOpen && !loading && (
            <div className="mobile-drawer">
              <MobileMenu items={menuTree} />
            </div>
          )}

          {/* <div className="nav-actions">
            <a href="tel:06802292746" className="btn btn-outline btn-sm">📞 Emergency</a>
          </div> */}
        </div>
      </nav>
    </>
  );
}
